import type { ReactNode } from 'react';
import { EventTypesEnum } from 'types/generated/client';
import { formatInVenueZone } from 'utils/shared/time/formatInVenueZone';
import { formatVenueTimeRange } from 'utils/shared/time/formatVenueTimeRange';
import ActivityBadge from 'components/ActivityBadge';
import Dot from 'components/Dot';
import GamePlayers from 'components/Find/GamePlayers';
import { isUnlimitedOpenPlay } from 'components/Find/gameFormat';
import type { FindGame } from 'components/Find/types';
import classNames from 'styles/utils/classNames';

interface Props {
  game: FindGame;
  /** 'stacked' (default): the modal's vertical summary. 'split': the hosted-card
   *  layout — from `lg` the players box moves left of the text block. */
  variant?: 'stacked' | 'split';
  /** Rendered after the date row on desktop split, after the players on mobile. */
  location?: ReactNode;
  /** `sm`: card-sized type (the pinned card on a request thread). Default is the modal's. */
  size?: 'md' | 'sm';
  /** `lg`: type and roster step up from lg, for Figma's wider "Modal M". */
  sizeDesktop?: 'lg';
  /** Host-only: an "Invite" pill under each open singles/doubles seat. */
  onInviteOpenSeat?: () => void;
  /** Host-only: the Open Play roster's "+ Invite players" action row. */
  onInvitePlayers?: () => void;
}

/** The small "•" separator between meta values. */
/** The card-style summary at the top of the modal: badge, price, heading, time, players. */
export default function MatchSummary({
  game,
  variant = 'stacked',
  location,
  size = 'md',
  sizeDesktop,
  onInviteOpenSeat,
  onInvitePlayers,
}: Props) {
  const isSplit = variant === 'split';
  const isSmall = size === 'sm';
  const isLargeAtDesktop = sizeDesktop === 'lg';
  const fmt = (date: string, pattern: string) => formatInVenueZone(date, pattern, game.timezone);

  return (
    <div
      className={classNames(
        'flex flex-col gap-ds-md',
        isSplit && 'lg:flex-row lg:items-center',
        isLargeAtDesktop && 'lg:gap-ds-sm',
      )}
    >
      {/* `contents` on mobile keeps the stacked order; a real info column at lg split. */}
      <div
        className={classNames(
          'contents',
          isSplit && 'lg:order-2 lg:flex lg:min-w-0 lg:flex-1 lg:flex-col lg:gap-ds-sm lg:pl-ds-lg',
        )}
      >
        <div className="flex items-center justify-between gap-ds-sm">
          {/* Games are badged by competitiveness ("Tennis Competitive"), not activity type. */}
          <ActivityBadge
            type={EventTypesEnum.OpenPlay}
            sport={game.sport}
            competitiveness={game.competitiveness}
            size="xs"
            sizeDesktop={isLargeAtDesktop ? 'sm' : undefined}
          />

          {game.priceFrom !== undefined && (
            <p className="flex shrink-0 items-baseline gap-ds-xs">
              {isSplit && (
                <span className="typography-product-text-card-desktop text-color-text-lightmode-placeholder dark:text-color-text-darkmode-placeholder max-lg:hidden">
                  from
                </span>
              )}
              <span
                className={classNames(
                  isSmall
                    ? 'typography-product-card-title-mobile'
                    : 'typography-product-subheading-mobile',
                  isLargeAtDesktop && 'lg:typography-product-heading-compact-desktop',
                  'text-color-text-lightmode-primary dark:text-color-text-darkmode-primary',
                )}
              >
                ${game.priceFrom}
              </span>
            </p>
          )}
        </div>

        <h2
          className={classNames(
            isSmall ? 'typography-product-card-title-mobile' : 'typography-product-heading-mobile',
            isLargeAtDesktop && 'lg:typography-product-heading-desktop',
            'text-color-text-lightmode-primary dark:text-color-text-darkmode-primary',
          )}
        >
          {game.title}
        </h2>

        <div
          className={classNames(
            isSmall ? 'typography-product-chips-filters' : 'typography-product-subheading-mobile',
            isLargeAtDesktop && 'lg:typography-product-subheading',
            'flex items-center gap-ds-xs text-color-text-lightmode-primary dark:text-color-text-darkmode-primary',
          )}
        >
          <span>{fmt(game.startDateTime, 'MMM d')}</span>
          <Dot />
          <span>{fmt(game.startDateTime, 'EEE')}</span>
          <Dot />
          <span>{formatVenueTimeRange(game.startDateTime, game.endDateTime, game.timezone)}</span>
        </div>

        {location && <div className={classNames(isSplit && 'max-lg:order-2')}>{location}</div>}
      </div>

      <div
        className={classNames(
          isSplit &&
            'lg:order-1 lg:w-[21.25rem] lg:shrink-0 lg:rounded-xl lg:border lg:border-color-border-input-lightmode lg:bg-color-bg-lightmode-primary lg:p-ds-sm lg:shadow-[0px_4px_8px_0px_rgba(0,0,0,0.08)] dark:lg:border-color-border-input-darkmode dark:lg:bg-color-bg-darkmode-primary',
        )}
      >
        <GamePlayers
          teams={game.teams}
          playersPerTeam={game.playersPerTeam}
          format={game.format}
          isUnlimited={isUnlimitedOpenPlay(game)}
          bordered={false}
          isLargeAtDesktop={isLargeAtDesktop}
          onInviteOpenSeat={onInviteOpenSeat}
          onInvitePlayers={onInvitePlayers}
        />
      </div>
    </div>
  );
}
